import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { Sun, Moon, MessageSquare, MessageSquareOff, Search, Bell, CalendarDays } from "lucide-react";
import API from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Header() {
  const navigate = useNavigate();
  const { theme, toggleTheme, chatOpen, setChatOpen, auditRun, auditRuns, loadRun } = useAudit();
  const { logout } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const filteredRuns = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return auditRuns.slice(0, 8);
    return auditRuns
      .filter((r) => String(r.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [auditRuns, searchText]);

  const switchAudit = useCallback(async (run) => {
    if (!run?.id) return;
    try {
      const loaded = await loadRun(run.id);
      setSearchOpen(false);
      setSearchText(run.name || "");
      navigate(`/phase/${loaded?.current_phase || 1}`);
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error("Your session expired. Please sign in again.");
        logout();
        navigate("/login", { replace: true });
        return;
      }
      if (error?.response?.status === 403) {
        toast.error("You do not have access to this audit.");
        return;
      }
      toast.error(error?.response?.data?.detail || "Failed to open audit");
    }
  }, [loadRun, logout, navigate]);

  const fetchNotifications = useCallback(async () => {
    try {
      const params = { limit: 8 };
      if (auditRun?.id) params.run_id = auditRun.id;
      const { data } = await API.get("/audit-logs", { params });
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      setNotifications([]);
    }
  }, [auditRun?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <div className="border-b border-white/30 bg-[#eef2ff]/85 backdrop-blur-xl px-6 py-5 shadow-[0_10px_35px_rgba(37,99,235,0.06)]" data-testid="header">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <div className="header-kicker inline-flex items-center gap-2 rounded-full border border-[#bfdbfe] bg-white/65 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.24em] text-[#1d4ed8]">
            <span className="h-2 w-2 rounded-full bg-[#3b82f6]" />
            {auditRun?.name ? `Active audit: ${auditRun.name}` : "Track controls, exceptions, and audit momentum across each phase."}
          </div>
          <h1 className="header-title mt-3 text-[32px] leading-none font-extrabold text-slate-900"> </h1>
          <p className="header-subtitle mt-2 text-sm text-slate-600">
           </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <div className="relative w-full min-[900px]:w-72">
            <Search className="w-4 h-4 text-[#3b82f6] absolute left-4 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search audits..."
              className="h-11 pl-11 border-[#bfdbfe] bg-white/85"
              value={searchText}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filteredRuns.length > 0) switchAudit(filteredRuns[0]);
              }}
            />
            {searchOpen && (
              <div className="header-search-results absolute z-50 mt-2 w-full rounded-3xl border border-[#bfdbfe] bg-white p-2 shadow-[0_18px_36px_rgba(29,78,216,0.12)]">
                {filteredRuns.length > 0 ? (
                  filteredRuns.map((run) => (
                    <button
                      key={run.id}
                      className="header-item quick-action w-full rounded-2xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-[#fff3e8]"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        switchAudit(run);
                      }}
                    >
                      {run.name}
                    </button>
                  ))
                ) : (
                  <p className="header-empty px-3 py-2 text-xs text-slate-500">No audits found</p>
                )}
              </div>
            )}
          </div>
          <Popover onOpenChange={(open) => open && fetchNotifications()}>
            <PopoverTrigger asChild>
              <div className="relative">
                <Button variant="ghost" size="icon" className="header-icon-btn h-10 w-10 bg-white/70 text-slate-600 hover:bg-white">
                  <Bell className="w-4 h-4" />
                </Button>
                {notifications.length > 0 && <span className="notification-pulse absolute right-1.5 top-1.5 w-2 h-2 rounded-full bg-[#3b82f6]" />}
              </div>
            </PopoverTrigger>
            <PopoverContent align="end" className="header-popover w-80 overflow-hidden rounded-[1.75rem] border-[#bfdbfe] p-0">
              <div className="border-b border-[#c7d2fe] bg-[#eef2ff] px-4 py-3">
                <p className="header-popover-title text-sm font-semibold text-slate-800">Notifications</p>
                <p className="header-subtitle text-xs text-slate-500">Recent audit activity</p>
              </div>
              <ScrollArea className="max-h-72">
                <div className="p-2 space-y-1">
                  {notifications.length === 0 ? (
                    <p className="header-empty text-xs text-slate-500 px-3 py-2">No new notifications</p>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        className="header-item quick-action w-full rounded-2xl px-3 py-3 text-left hover:bg-[#fff3e8]"
                        onClick={() => navigate("/logs")}
                      >
                        <p className="header-item-title text-xs font-medium text-slate-800">{String(n.action || "audit_update").replace(/_/g, " ")}</p>
                        <p className="header-item-sub text-[11px] text-slate-500 truncate">{n.audit_run_id ? `Audit: ${n.audit_run_id}` : "System activity"}</p>
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
          <div className="header-date-pill inline-flex h-10 items-center gap-2 rounded-full border border-[#f2d6bf] bg-white/80 px-4 text-sm text-slate-600">
            <CalendarDays className="w-4 h-4" />
            Mar 19, 2026
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => setChatOpen(!chatOpen)}
            data-testid="toggle-chat-btn"
            aria-label="Toggle chat panel"
            className="h-10 gap-1.5 border-[#f2d6bf] px-4 text-xs"
          >
            {chatOpen ? <MessageSquareOff className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
            {chatOpen ? "Hide Chat" : "Chat"}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
            className="h-10 gap-1.5 border-[#f2d6bf] px-4 text-xs"
          >
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            {theme === 'dark' ? "Light" : "Dark"}
          </Button>
        </div>
      </div>
    </div>
  );
}
